import {
  SpecificBlockData,
  Block,
  DEFAULT_FREE_MODEL,
} from '@basehub/data/replicache/index'
import { SYSTEM_AGENTS } from '@basehub/data/replicache/schema/block/agents'
import { getId, getIdempotentId } from '@basehub/utils/id/index'
import { invariant } from '@epic-web/invariant'

type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>

export type GetNewBlockDataByTypeParams = AtLeast<Block, 'id' | 'type'>

export function getNewBlockDataByType<
  T extends GetNewBlockDataByTypeParams,
  R extends Extract<SpecificBlockData, { type: T['type'] }> & { id: string },
>(data: T): R {
  let blockSpecificStuff: undefined | SpecificBlockData = undefined

  switch (data.type) {
    case 'root':
      invariant(
        data.branchId,
        'branchId is required to create a new root block'
      )
      blockSpecificStuff = {
        ...data,
        branchId: data.branchId,
        type: data.type,
        value: data.value ?? {},
        constraints: data.constraints ?? {},
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'document':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? {},
        constraints: data.constraints ?? {},
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'text':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? null,
        constraints: data.constraints ?? {
          isRequired: false,
          maxLength: null,
          minLength: null,
          regexPattern: null,
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'icon':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? null,
        constraints: data.constraints ?? {
          isRequired: false,
          acceptedVariantSets: null,
          allowedLibraries: null,
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'rich-text':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? { state: '' },
        constraints: data.constraints ?? {
          isRequired: false,
          forbiddenNodes: null,
          allowedComponents: null,
          layout: 'multi-line',
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'comment':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? {
          state: '',
        },
        constraints: data.constraints ?? {},
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'select':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? [],
        constraints: data.constraints ?? {
          isRequired: false,
          acceptedValues: [],
          multiple: false,
          max: 1000,
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'number':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? null,
        constraints: data.constraints ?? {
          isRequired: false,
          maxValue: null,
          minValue: null,
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'date':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? null,
        constraints: data.constraints ?? {
          isRequired: false,
        },
        typeOptions: data.typeOptions ?? { useTime: false },
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'color':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? '#000000',
        constraints: data.constraints ?? {
          isRequired: false,
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'og-image':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? {},
        constraints: data.constraints ?? {
          layersData: [
            {
              type: 'shape',
              positionX: 0,
              positionY: 0,
              positionZ: 0,
              width: 1200,
              height: 630,
              rotation: 0,
              opacity: 1,
              cornerRadius: 0,
              color: '#161616',
            },
          ],
          layersDataVersion: 1,
          showTemplates: true,
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'media':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? { status: 'idle' },
        constraints: data.constraints ?? {
          isRequired: false,
          allowedMimeTypes: {
            image: true,
            video: true,
            audio: true,
            file: true,
          },
          maxSize: null,
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'boolean':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? false,
        constraints: data.constraints ?? { acceptedVariantSets: null },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'code-snippet':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? {
          code: '',
          language: 'plainText',
        },
        constraints: data.constraints ?? {
          isRequired: false,
          allowedLanguages: null,
        },
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'list':
      if (!data.typeOptions) {
        throw new Error('typeOptions is required to create a new "list" block.')
      }
      blockSpecificStuff = {
        ...data,
        type: data.type,
        typeOptions: data.typeOptions,
        value: data.value ?? { views: [] },
        constraints: data.constraints ?? {
          isRequired: false,
          maxRows: null,
          minRows: null,
        },
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'instance':
      if (!data.typeOptions) {
        throw new Error(
          'typeOptions is required to create a new "instance" block.'
        )
      }
      blockSpecificStuff = {
        ...data,
        type: data.type,
        typeOptions: data.typeOptions,
        value: data.value ?? {},
        constraints: data.constraints ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'component':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        typeOptions: data.typeOptions ?? {
          displayTitle: '',
          displayDescription: '',
          displayIcon: '',
        },
        value: data.value ?? {},
        constraints: data.constraints ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'reference':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
        value: data.value ?? {
          targetBlockIds: [],
        },
        constraints: data.constraints ?? {
          isRequired: false,
          acceptedTypes: null,
          multiple: false,
          disableCreateNew: null,
          disableLinkExisting: null,
          startCollapsed: null,
        },
      }
      break
    case 'variant-set':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
        value: data.value ?? {
          variants: [
            {
              id: getId(),
              label: 'Default',
              apiName: '',
              isDefault: true,
              color: 'var(--presence-raw-default)',
              hidden: false,
            },
          ],
        },
        constraints: data.constraints ?? {},
      }
      break
    case 'event':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? {},
        constraints: data.constraints ?? {
          schema: {},
        },
        typeOptions: data.typeOptions ?? { view: 'table' },
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'workflow':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? {
          trigger: {
            type: 'commit',
            events: ['created'],
          },
          activeRuns: [],
          actions: {
            [`0-${getIdempotentId({
              idSeed: `${data.id}-action`,
            })}`]: {
              type: 'webhook',
              url: '',
            },
          },
        },
        constraints: data.constraints ?? {},
        typeOptions: data.typeOptions ?? {},
        searchConfig: data.searchConfig ?? { index: false },
      }
      break
    case 'agent':
      blockSpecificStuff = {
        ...data,
        type: data.type,
        value: data.value ?? {
          state: JSON.stringify({
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    text: 'You are a helpful assistant called {{ agentName }}.',
                  },
                ],
              },
              {
                type: 'paragraph',
                content: [
                  {
                    type: 'text',
                    text: '<guidelines>',
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'text',
                    text: '1. Use the content repository you have access to, to keep responses grounded.',
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'text',
                    text: `2. Don't use emoji unless it's necessary.`,
                  },
                  {
                    type: 'hardBreak',
                  },
                  {
                    type: 'text',
                    text: '</guidelines>',
                  },
                ],
              },
            ],
          }),
        },
        constraints: data.constraints ?? SYSTEM_AGENTS[0].constraints,
        typeOptions: data.typeOptions ?? {
          model: DEFAULT_FREE_MODEL,
          avatar: null,
        },
        searchConfig: { index: false },
      }
      break
    default:
      throw new Error(`Unknown block type: ${JSON.stringify(data)}`)
  }

  if (!blockSpecificStuff) {
    throw new Error(
      `blockSpecificStuff could not be resolved ${blockSpecificStuff}`
    )
  }

  return blockSpecificStuff as R
}
